// Agent Dashboard — subscription status, trust score, invitation inbox
import { useCallback, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAgentProfile } from "@/hooks/useAgentProfile";
import { api } from "@/services/api";
import { PageHeader, Card, Button, LoadingSpinner, PPRABadge, StatusBadge, EmptyState } from "@/components/shared";
import {
  ProvinceLabels,
  PropertyTypeLabels,
  TimelineLabels,
  type OwnerRequest,
  type PaginatedResponse,
  type Subscription,
} from "@/types";
import { formatDate } from "@/utils/format";
import { Award, CheckCircle2, Clock, CreditCard, Home } from "lucide-react";
import { InvitationInbox } from "@/components/agent/InvitationInbox";
import { FfcExpiryWarning } from "@/components/agent/FfcExpiryWarning";

type PendingResponseRequest = Pick<
  OwnerRequest,
  | "id"
  | "suburb"
  | "province"
  | "property_type"
  | "price_band_min"
  | "price_band_max"
  | "timeline"
  | "created_at"
  | "response_count"
  | "my_response_status"
>;

export function selectPendingResponses(requests: PendingResponseRequest[]): PendingResponseRequest[] {
  return requests.filter((request) => request.my_response_status === "responded");
}

export function AgentDashboardPage() {
  const { profile, isLoading: profileLoading, refresh: refreshProfile } = useAgentProfile();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [pendingResponses, setPendingResponses] = useState<PendingResponseRequest[]>([]);
  const [pendingResponsesLoading, setPendingResponsesLoading] = useState(false);

  const refreshSubscription = useCallback(async () => {
    const res = await api.get<{ subscription: Subscription | null }>("/subscriptions/me").catch(() => null);
    setSubscription(res?.subscription ?? null);
  }, []);

  const refreshDashboardStats = useCallback(async () => {
    await Promise.all([refreshProfile(), refreshSubscription()]);
  }, [refreshProfile, refreshSubscription]);

  useEffect(() => {
    async function load() {
      try {
        await refreshSubscription();

        setPendingResponsesLoading(true);
        const requests = await api.get<PaginatedResponse<PendingResponseRequest>>("/browse-requests", { limit: 50 });
        setPendingResponses(selectPendingResponses(requests.data));
      } catch {}
      finally {
        setPendingResponsesLoading(false);
      }
    }
    if (profile) load();
  }, [profile, refreshSubscription]);

  if (profileLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  if (!profile) {
    return (
      <div>
        <PageHeader title="Agent Dashboard" />
        <EmptyState
          icon={Award}
          title="Complete your registration"
          description="Create your agent profile to start browsing owner requests."
          action={<Link to="/agent/register"><Button>Register</Button></Link>}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Agent Dashboard" description={`Welcome back, ${profile.display_name}`} />

      {/* FFC Expiry Warning */}
      {profile.ffc_expiry_date && (
        <div className="mb-6">
          <FfcExpiryWarning ffcExpiryDate={profile.ffc_expiry_date} />
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <div>
            <p className="text-muted">Trust Score</p>
            <p className="stat-value">{Number(profile.trust_score).toFixed(0)}</p>
          </div>
        </Card>
        <Card>
          <p className="text-muted">Responses</p>
          <p className="stat-value">{profile.total_responses}</p>
        </Card>
        <Card>
          <p className="text-muted">Unlocks Won</p>
          <p className="stat-value">{profile.total_unlocks}</p>
        </Card>
        <Card>
          <p className="text-muted">Avg Response</p>
          <p className="stat-value">
            {profile.avg_response_time_mins ? `${profile.avg_response_time_mins} min` : "—"}
          </p>
        </Card>
      </div>

      {/* Profile & Subscription */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading-section">Profile</h3>
            <Link to="/agent/register" className="text-xs text-sun hover:underline">Edit</Link>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/50">Agency</span><span>{profile.agency_name}</span></div>
            <div className="flex justify-between"><span className="text-white/50">PPRA</span><PPRABadge status={profile.ppra_status} /></div>
            <div className="flex justify-between"><span className="text-white/50">Experience</span><span>{profile.years_experience || "—"} years</span></div>
          </div>
        </Card>
        <Card>
          <h3 className="heading-section mb-3 flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Subscription
          </h3>
          {subscription ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-white/50">Tier</span><StatusBadge status={subscription.tier} variant="info" /></div>
              <div className="flex justify-between"><span className="text-white/50">Status</span><StatusBadge status={subscription.status} variant="success" /></div>
              <div className="bg-slateblue/50 rounded-lg p-3 mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white/50 text-xs">Leads remaining</span>
                  <span className="text-xs text-white/40">{subscription.leads_used} of {subscription.lead_limit === -1 ? '∞' : subscription.lead_limit} used</span>
                </div>
                <p className={`text-2xl font-bold ${subscription.leads_remaining <= 2 && subscription.leads_remaining !== -1 ? 'text-coral' : 'text-mint'}`}>
                  {subscription.leads_remaining === -1 ? '∞' : subscription.leads_remaining}
                </p>
                {subscription.leads_remaining <= 2 && subscription.leads_remaining !== -1 && (
                  <p className="text-xs text-coral/70 mt-1">Upgrade for more leads</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-muted">No active subscription. <Link to="/agent/subscribe" className="link">Subscribe now</Link></p>
          )}
        </Card>
      </div>

      {/* Invitations */}
      <PendingResponsesSection responses={pendingResponses} isLoading={pendingResponsesLoading} />
      <InvitationInbox onInvitationsChanged={refreshDashboardStats} />
    </div>
  );
}

function PendingResponsesSection({
  responses,
  isLoading,
}: {
  responses: PendingResponseRequest[];
  isLoading: boolean;
}) {
  return (
    <section className="space-y-4 mb-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-bold text-white">Pending Responses ({responses.length})</h2>
        <Link to="/agent/browse" className="text-xs text-sun hover:underline">
          Browse requests
        </Link>
      </div>

      {isLoading ? (
        <Card>
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        </Card>
      ) : responses.length === 0 ? (
        <Card>
          <p className="text-white/50 text-center py-4">No pending responses</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {responses.map((response) => (
            <Card key={response.id} className="border-l-4 border-l-teal-400">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Home className="h-4 w-4 text-teal-300" />
                    <h3 className="font-semibold text-white">
                      {response.suburb}, {ProvinceLabels[response.province]}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/50">
                    <span>{PropertyTypeLabels[response.property_type]}</span>
                    <span>
                      R{(response.price_band_min / 1000000).toFixed(1)}M - R{(response.price_band_max / 1000000).toFixed(1)}M
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {TimelineLabels[response.timeline]}
                    </span>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <div className="inline-flex items-center gap-1.5 rounded-md border border-teal-400/20 bg-teal-400/10 px-2 py-1 text-xs font-medium text-teal-300">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Awaiting owner
                  </div>
                  <p className="mt-2 text-xs text-white/40">Listed {formatDate(response.created_at)}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
